// Campaign attribution from the landing URL, kept for the browser session so a reload or the error
// page's "Try again" keeps it. Last touch wins: a link with any campaign parameter replaces what the
// session had. A visit without a campaign gets the brand's Web Organic campaign (cam_Campaigns).
//
//   campaignId | cid                       Penrose CampaignId
//   ttclid | gclid | fbclid | clickId      the channel's click id (TikTok, Google, Meta, generic);
//                                          gclid is also kept as googleClickId
//   publisherId, subPublisherId
//   transaction_id                         affiliate (Everflow) format {clickId}-{campaignId}-{publisherId};
//                                          clickId may contain '-', so the ids are taken from the right
// An explicit campaignId / click id / publisherId wins over the one inside transaction_id.
const SESSION_KEY = 'wr-lp-attribution'
const CLICK_PARAMS = ['ttclid', 'gclid', 'fbclid', 'clickId']
// Tracking ids are printable ASCII. Anything else (an "…" from a link copied out of a chat window,
// a space, a smart quote) means the link was mangled, so the value is dropped rather than guessed at.
const TRACKING_ID = /^[\x21-\x7E]{1,500}$/
// A Penrose campaign id: digits, no leading zero (so a cut-off "…-01-2610…" cannot become campaign 1).
const CAMPAIGN_ID = /^[1-9]\d{0,9}$/

const clean = (value) => (value && TRACKING_ID.test(value) ? value : null)
const cleanCampaign = (value) => (value && CAMPAIGN_ID.test(value) ? value : null)

// The affiliate triple is all-or-nothing: if any segment is unusable the link was cut, and using the
// rest would attribute to the wrong campaign.
function fromTransactionId(value) {
  if (!value) return { discarded: false }
  const parts = value.split('-')
  const publisherId = parts.pop() || null
  const campaignId = parts.pop() || null
  const clickId = parts.join('-') || null
  const valid = TRACKING_ID.test(value) && clean(clickId) && cleanCampaign(campaignId) && clean(publisherId)
  return valid ? { clickId, campaignId, publisherId, discarded: false } : { discarded: true }
}

function fromUrl(query) {
  const transactionId = query.get('transaction_id') || null
  const affiliate = fromTransactionId(transactionId)
  const clickParam = CLICK_PARAMS.find((key) => clean(query.get(key)))
  const params = {
    transactionId: affiliate.discarded ? null : transactionId,
    campaignId: cleanCampaign(query.get('campaignId')) || cleanCampaign(query.get('cid')) || affiliate.campaignId || null,
    clickId: (clickParam && query.get(clickParam)) || affiliate.clickId || null,
    googleClickId: clean(query.get('gclid')),
    publisherId: clean(query.get('publisherId')) || affiliate.publisherId || null,
    subPublisherId: clean(query.get('subPublisherId'))
  }
  if (affiliate.discarded) params.discarded = 'truncated transaction_id'
  return Object.values(params).some(Boolean) ? params : null
}

function readSession() {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_KEY)) || null
  } catch {
    return null
  }
}

function saveSession(params) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(params))
  } catch {
    // private mode / storage blocked: attribution still applies to this page load
  }
}

export default function getCampaignParametersFromUrl(organicCampaignId = null) {
  const fromLink = fromUrl(new URLSearchParams(window.location.search))
  if (fromLink) saveSession(fromLink)
  const found = fromLink || readSession()
  let source = fromLink ? 'url' : found ? 'session' : null
  const params = found || { transactionId: null, campaignId: null, clickId: null, googleClickId: null, publisherId: null, subPublisherId: null }
  if (params.discarded) source = `${source}, discarded: ${params.discarded}`
  if (params.campaignId) return { ...params, source }
  return { ...params, campaignId: organicCampaignId, source: source ? `${source}, organic campaign` : 'organic default' }
}
